import { db, DEFAULT_SETTINGS } from '../db';
import { saveAs } from 'file-saver';
import { z } from 'zod';

const TABLES = ['settings', 'categories', 'units', 'products', 'variants', 'customers', 'suppliers', 'invoices', 'invoiceItems', 'payments', 'stockTransactions', 'meta'];
const numericId = z.number().int().positive();
const finite = z.number().finite();
const record = z.object({ id: numericId }).passthrough();
const schemas = {
  products: record.extend({ name: z.string().min(1), sku: z.string().min(1), unit: z.string().min(1), currentStock: finite, purchasePrice: finite.nonnegative(), sellingPrice: finite.nonnegative(), minStock: finite.nonnegative(), gstRate: z.union([z.literal(0), z.literal(5), z.literal(12), z.literal(18), z.literal(28)]), active: z.union([z.literal(0), z.literal(1)]), piecesStock: finite.int().nullable().optional(), minPiecesStock: finite.nonnegative().int().nullable().optional() }),
  invoices: record.extend({ number: z.string().min(1), date: z.string().datetime(), customerName: z.string(), status: z.enum(['active', 'cancelled']), grandTotal: finite.nonnegative(), paidAmount: finite.nonnegative(), balance: finite.nonnegative(), subtotal: finite.nonnegative(), taxableAmount: finite, taxAmount: finite.nonnegative() }),
  invoiceItems: record.extend({ invoiceId: numericId, productId: numericId, name: z.string(), unit: z.string(), qty: finite.positive(), rate: finite.nonnegative(), total: finite.nonnegative() }),
  payments: record.extend({ invoiceId: numericId, amount: finite.positive(), date: z.string().datetime() }),
  stockTransactions: record.extend({ productId: numericId, date: z.string().datetime(), type: z.enum(['in', 'out']), previousStock: finite, quantity: finite.nonnegative(), newStock: finite }),
  settings: z.object({ id: z.literal('main'), businessName: z.string().min(1), invoicePrefix: z.string().min(1), invoiceCounter: z.number().int().nonnegative(), language: z.enum(['en', 'hi']), allowNegativeStock: z.boolean().optional(), gstEnabled: z.boolean().optional(), defaultGstRate: z.union([z.literal(0), z.literal(5), z.literal(12), z.literal(18), z.literal(28)]).optional(), printFormat: z.enum(['A4', 'thermal']).optional(), currency: z.string().min(1).optional() }).passthrough(),
  categories: record.extend({ name: z.string().min(1) }),
  units: record.extend({ name: z.string().min(1) }),
  meta: z.object({ key: z.string().min(1) }).passthrough(),
  customers: record.extend({ name: z.string().min(1) }),
  suppliers: record.extend({ name: z.string().min(1) }),
};

export function validateBackup(text) {
  let parsed;
  try { parsed = JSON.parse(text); } catch { throw new Error('Invalid backup file'); }
  if (parsed?.app !== 'HardwareShopDB' || ![1, 2].includes(parsed.version) || !parsed.data) throw new Error('Invalid backup file');
  for (const table of TABLES) {
    const result = z.array(schemas[table] || record).safeParse(parsed.data[table]);
    if (!result.success) throw new Error(`Invalid backup file: ${table}`);
    const key = table === 'meta' ? 'key' : 'id';
    if (new Set(result.data.map(r => r[key])).size !== result.data.length) throw new Error(`Invalid backup file: duplicate ${table} IDs`);
  }
  const data = parsed.data;
  if (data.settings.length !== 1) throw new Error('Invalid backup file: missing shop settings');
  if (new Set(data.products.map(p => p.sku)).size !== data.products.length || new Set(data.invoices.map(i => i.number)).size !== data.invoices.length) throw new Error('Invalid backup file: duplicate SKU or invoice number');
  const invoiceIds = new Set(data.invoices.map(i => i.id));
  if (data.invoiceItems.some(it => !invoiceIds.has(it.invoiceId)) || data.payments.some(p => !invoiceIds.has(p.invoiceId))) throw new Error('Invalid backup file: orphan invoice records');
  // Historical item snapshots remain valid even if an older app deleted their product.
  return parsed;
}

export async function exportBackup() {
  const data = await db.transaction('r', db.tables, async () => {
    const snapshot = {};
    for (const table of TABLES) snapshot[table] = await db.table(table).toArray();
    return snapshot;
  });
  const exportedAt = new Date().toISOString();
  const payload = { app: 'HardwareShopDB', version: 2, exportedAt, data };
  saveAs(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }), `hardware-shop-backup-${exportedAt.slice(0, 10)}.json`);
  await db.meta.put({ key: 'lastBackup', date: exportedAt });
}

export async function importBackup(text) {
  const parsed = validateBackup(text);
  await db.transaction('rw', db.tables, async () => {
    for (const table of TABLES) {
      await db.table(table).clear();
      if (parsed.data[table].length) await db.table(table).bulkPut(parsed.data[table]);
    }
    await db.settings.put({ ...DEFAULT_SETTINGS, ...parsed.data.settings[0], demoDataLoaded: true });
  });
  return true;
}

